
import { getRemoteParticipants } from '../base/participants/functions';
import { StateListenerRegistry } from '../base/redux';

import { SYNTHETIC_TRANSLATED_SOURCE_MARKER } from './constants';
import {
    getEffectiveTranslationLanguage,
    getListenerVoiceMode,
    getOriginalAudioTrackByParticipant
} from './functions.web';
import logger from './logger';

/**
 * The presence property carrying this listener's own list of speakers they want to hear translated. Jicofo reads
 * it as `jitsi_participant_translated_voice_speakers` and pairs it with the listener's translation language to
 * decide which synthetic sources to create (see TranslationOrchestrator.translationDemandByListener - it is the
 * `else` branch, the one that is *not* a room-wide moderator override).
 */
const P_NAME_TRANSLATED_VOICE_SPEAKERS = 'translated_voice_speakers';

let _lastVoiceModeEnabled;
let _lastLanguage;
let _lastRemoteParticipants;
let _lastVoiceModeByParticipant;
let _lastTranslatedVoiceEnabled;
let _cachedSpeakers = [];

/**
 * The remote speakers the local listener wants to hear translated - their own choice alone.
 *
 * Deliberately published as a property of *this* participant rather than sent as a `voice-mode-command`. A command
 * is broadcast to the room and makes the targeted speaker publish `voice_mode` on their own presence, which Jicofo
 * applies to every listener that has a translation language. That made one listener's toggle audible to everyone,
 * and made two listeners with opposite toggles fight: each kept re-sending the opposite command for the same
 * speaker, flipping that speaker's shared presence back and forth. Keeping the choice in the listener's own
 * presence removes both problems by construction - nobody ever writes anyone else's state.
 *
 * @param {Object} state - The redux state.
 * @returns {Array<string>} sorted speaker ids, empty when translated voice is off or unavailable.
 */
function _computeTranslatedVoiceSpeakers(state) {
    const voiceModeEnabled = state['features/base/config']?.transcription?.voiceMode?.enabled !== false;
    const language = getEffectiveTranslationLanguage(state);

    if (!voiceModeEnabled || !language) {
        if (_cachedSpeakers.length === 0) {
            return _cachedSpeakers;
        }
        _cachedSpeakers = [];

        return _cachedSpeakers;
    }

    const { _voiceModeByParticipant, _translatedVoiceEnabled } = state['features/subtitles'];
    const remoteParticipants = getRemoteParticipants(state);

    if (voiceModeEnabled === _lastVoiceModeEnabled
        && language === _lastLanguage
        && remoteParticipants === _lastRemoteParticipants
        && _voiceModeByParticipant === _lastVoiceModeByParticipant
        && _translatedVoiceEnabled === _lastTranslatedVoiceEnabled) {
        return _cachedSpeakers;
    }

    _lastVoiceModeEnabled = voiceModeEnabled;
    _lastLanguage = language;
    _lastRemoteParticipants = remoteParticipants;
    _lastVoiceModeByParticipant = _voiceModeByParticipant;
    _lastTranslatedVoiceEnabled = _translatedVoiceEnabled;

    const nextSpeakers = remoteParticipants
        .filter(participant =>
            getListenerVoiceMode(_voiceModeByParticipant, _translatedVoiceEnabled, participant.id) === 'translated')
        .map(participant => participant.id)
        .sort();

    if (_cachedSpeakers.length === nextSpeakers.length
        && _cachedSpeakers.every((id, idx) => id === nextSpeakers[idx])) {
        return _cachedSpeakers;
    }

    _cachedSpeakers = nextSpeakers;

    return _cachedSpeakers;
}

let _lastSubLanguage;
let _lastSubSpeakers;
let _lastSubTracks;
let _cachedSubscription = {
    include: [],
    exclude: []
};

/**
 * Computes, from the local listener's own translated-voice toggle and translation language, which synthetic
 * translated-voice sources to receive (and which corresponding original sources to drop in favor of them).
 * Sources not named in `include`/`exclude` keep flowing normally ({@code all: true}) - only the speakers this
 * listener has chosen to hear translated are affected.
 *
 * @param {Object} state - The redux state.
 * @returns {{ include: Array<string>, exclude: Array<string> }}
 */
function _computeAudioSubscription(state) {
    const selectedLanguage = getEffectiveTranslationLanguage(state);

    if (!selectedLanguage) {
        if (_cachedSubscription.include.length === 0 && _cachedSubscription.exclude.length === 0) {
            return _cachedSubscription;
        }
        _cachedSubscription = {
            include: [],
            exclude: []
        };

        return _cachedSubscription;
    }

    const speakers = _computeTranslatedVoiceSpeakers(state);
    const tracks = state['features/base/tracks'];

    if (selectedLanguage === _lastSubLanguage
        && speakers === _lastSubSpeakers
        && tracks === _lastSubTracks) {
        return _cachedSubscription;
    }

    _lastSubLanguage = selectedLanguage;
    _lastSubSpeakers = speakers;
    _lastSubTracks = tracks;

    const include = [];
    const exclude = [];

    for (const participantId of speakers) {
        include.push(`${participantId}${SYNTHETIC_TRANSLATED_SOURCE_MARKER}${selectedLanguage}`);

        const originalTrack = getOriginalAudioTrackByParticipant(tracks, participantId);
        const sourceName = originalTrack?.mhTrack?.getSourceName?.();

        if (sourceName) {
            exclude.push(sourceName);
        }
    }

    include.sort();
    exclude.sort();

    const sameInclude = _cachedSubscription.include.length === include.length
        && _cachedSubscription.include.every((item, i) => item === include[i]);
    const sameExclude = _cachedSubscription.exclude.length === exclude.length
        && _cachedSubscription.exclude.every((item, i) => item === exclude[i]);

    if (sameInclude && sameExclude) {
        return _cachedSubscription;
    }

    _cachedSubscription = {
        include,
        exclude
    };

    return _cachedSubscription;
}

/**
 * Logged once rather than on every state change, so a missing API does not bury the console.
 */
let _warnedAboutMissingAudioSubscription = false;

/**
 * Pushes the computed audio subscription to the bridge whenever the local listener's toggle, their selected
 * translation language, the set of remote participants, or the audio track list changes.
 */
StateListenerRegistry.register(
    /* selector */ state => _computeAudioSubscription(state),
    /* listener */ ({ include, exclude }, store) => {
        const conference = store.getState()['features/base/conference']?.conference;

        if (!conference) {
            return;
        }

        // Feature-detected because lib-meet-hour has no audio equivalent of setReceiverConstraints: it exposes
        // only per-source *video* constraints, and the bridge forwards every unmuted audio source regardless. An
        // unguarded call threw a TypeError on every toggle and every language change, which StateListenerRegistry
        // caught and logged but which meant this listener never completed.
        if (typeof conference.setReceiverAudioSubscription !== 'function') {
            if (!_warnedAboutMissingAudioSubscription) {
                _warnedAboutMissingAudioSubscription = true;
                logger.warn('conference.setReceiverAudioSubscription is unavailable; translated-voice sources'
                    + ' are left to the default audio forwarding of the bridge, and the original track is'
                    + ' silenced in the UI instead.');
            }

            return;
        }

        logger.debug(`Updating receiver audio subscription: include=${include} exclude=${exclude}`);

        conference.setReceiverAudioSubscription({
            all: true,
            include,
            exclude
        });
    },
    { deepEquals: true }
);

/**
 * Publishes the listener's own speaker list so Jicofo creates (and signals back) only the translated sources this
 * listener actually asked for. A listener with the toggle off publishes an empty list and therefore gets nothing,
 * without affecting anybody else.
 */
StateListenerRegistry.register(
    /* selector */ state => _computeTranslatedVoiceSpeakers(state),
    /* listener */ (speakers, store) => {
        const conference = store.getState()['features/base/conference']?.conference;

        if (!conference) {
            return;
        }

        logger.debug(`Updating translated voice speakers: ${speakers}`);

        conference.setLocalParticipantProperty(P_NAME_TRANSLATED_VOICE_SPEAKERS, speakers.join(','));
    },
    { deepEquals: true }
);
